import { ICommonSettings, INotifyActionsDefaultDurationSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigNotifyActionsDefaultDurationSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: INotifyActionsDefaultDurationSettings;
}

export interface IConfigNotifyActionsDefaultDurationSettingsProps {
  parentConfigValue?: string | null;
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingNotifyActionDurationId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigNotifyActionsDefaultDurationSettings extends React.PureComponent<
  IConfigNotifyActionsDefaultDurationSettingsProps,
  IConfigNotifyActionsDefaultDurationSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigNotifyActionsDefaultDurationSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigNotifyActionsDefaultDurationSettingsProps & IWithSDProps) {
    if (prevProps.editingNotifyActionDurationId !== this.props.editingNotifyActionDurationId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingNotifyActionDurationId) return;

    const editingConfig = this.props.commonSettings.notifyActionsDurationsSettings?.find(
      (x) => x.id === this.props.editingNotifyActionDurationId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const notifyActionsDurationsSettings = this.props.commonSettings?.notifyActionsDurationsSettings;
    if (!notifyActionsDurationsSettings) return;

    const isSettingAlreadyExist =
      notifyActionsDurationsSettings.some(
        (x) => x.name === value && x.id !== this.props.editingNotifyActionDurationId
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Notify Actions Default Duration Settings Name. is already exist.");
  };

  integerValidator = async (_rule, value, itemName: string) => {
    if (!value) return;
    const val = Number(value);
    if (!Number.isInteger(val)) throw new Error(`${itemName} Duration value should be an integer.`);
    if (val < 0) throw new Error(`${itemName} Duration value should be positive.`);
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: INotifyActionsDefaultDurationSettings;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      successDuration: data.successDuration,
      infoDuration: data.infoDuration,
      warningDuration: data.warningDuration,
      errorDuration: data.errorDuration,
      defaultDuration: data.defaultDuration
    };
    if (this.props.editingNotifyActionDurationId) {
      obj.id = this.props.editingNotifyActionDurationId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "notifyActionsDurationsSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "notifyActionsDurationsSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingNotifyActionDurationId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingNotifyActionDurationId || "",
      configKey: "notifyActionsDurationsSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Notify Actions Default Duration setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Notify Actions Default Duration setting"
        secondHeader="Notify Actions Default Duration setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingNotifyActionDurationId ? this.deleteSetting : undefined}
            deleteTestId="notifyActionsSettingDeleteButton"
            submitTestId="notifyActionsSettingCreateButton"
            submitLabel={this.props.editingNotifyActionDurationId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configNotifyActionDurationForm"
          />
        }
      >
        <Form
          id="configNotifyActionDurationForm"
          name="notifyActionsDurationsSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription(
              "appsettings_notifyactionsdefaultdurationsettingpopup_description"
            )}
          >
            <ConfigField
              label="Name"
              required
              tooltipContentKey="appsettings_notifyactionsdefaultdurationsettingpopup_name"
            >
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Notify Actions Default Duration Name." },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Notify Actions Default Duration Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="notifyActionsSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Default Duration (seconds)"
              tooltipContentKey="appsettings_notifyactionsdefaultdurationsettingpopup_defaultduration"
            >
              <Form.Item
                name="defaultDuration"
                rules={[{ validator: (rule, value) => this.integerValidator(rule, value, "Default") }]}
              >
                <KuikaInput size="md" type="number" min={0} placeholder="5" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Success Duration (seconds)"
              tooltipContentKey="appsettings_notifyactionsdefaultdurationsettingpopup_successduration"
            >
              <Form.Item
                name="successDuration"
                rules={[{ validator: (rule, value) => this.integerValidator(rule, value, "Success") }]}
              >
                <KuikaInput size="md" type="number" min={0} placeholder="5" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Info Duration (seconds)"
              tooltipContentKey="appsettings_notifyactionsdefaultdurationsettingpopup_infoduration"
            >
              <Form.Item
                name="infoDuration"
                rules={[{ validator: (rule, value) => this.integerValidator(rule, value, "Info") }]}
              >
                <KuikaInput size="md" type="number" min={0} placeholder="7" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Warning Duration (seconds)"
              tooltipContentKey="appsettings_notifyactionsdefaultdurationsettingpopup_warningduration"
            >
              <Form.Item
                name="warningDuration"
                rules={[{ validator: (rule, value) => this.integerValidator(rule, value, "Warning") }]}
              >
                <KuikaInput size="md" type="number" min={0} placeholder="15" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Error Duration (seconds)"
              tooltipContentKey="appsettings_notifyactionsdefaultdurationsettingpopup_errorduration"
            >
              <Form.Item
                name="errorDuration"
                rules={[{ validator: (rule, value) => this.integerValidator(rule, value, "Error") }]}
              >
                <KuikaInput size="md" type="number" min={0} placeholder="Visible until close" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
